import React, { useEffect, useRef } from "react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { WorkbookHero } from "@/components/workbook/WorkbookHero";
import { WorkbookMiddleSections } from "@/components/workbook/WorkbookMiddleSections";
import { WorkbookBottomSections } from "@/components/workbook/WorkbookBottomSections";
import SEOHead from "@/components/SEOHead";
import { trackEvent } from "@/lib/pixel";

const Workbook = () => {
  // Fire the standard Meta ViewContent event exactly once when the visitor
  // reaches the Workbook page. A ref guard prevents duplicate fires from
  // React rerenders or Strict Mode double-invocation.
  const viewContentFired = useRef(false);
  useEffect(() => {
    if (viewContentFired.current) return;
    viewContentFired.current = true;
    trackEvent("ViewContent", {
      content_name: "From Burnout to Becoming Workbook",
      content_type: "product",
      content_ids: ["workbook"],
    });
  }, []);

  return (
    <>
      <SEOHead
        title="From Burnout to Becoming Workbook: 64 Guided Pages by Ieshia Danielle"
        description="Name the source of your burnout across the six B's and write a practical plan forward. 64 guided pages, physical workbook plus instant digital access."
        canonical="https://iamieshiadanielle.com/programs/workbook"
        ogImage="https://vibe.filesafe.space/1784937135036191007/attachments/dd434134-7119-4daf-be69-edf4d039f7bb.png"
        schemaJson={[
          {
            "@type": "WebPage",
            "@id": "https://iamieshiadanielle.com/programs/workbook#webpage",
            url: "https://iamieshiadanielle.com/programs/workbook",
            name: "From Burnout to Becoming Workbook: 64 Guided Pages by Ieshia Danielle",
            isPartOf: { "@id": "https://iamieshiadanielle.com/#website" },
            about: { "@id": "https://iamieshiadanielle.com/#person" },
            inLanguage: "en-US",
          },
          {
            "@type": "BreadcrumbList",
            itemListElement: [
              {
                "@type": "ListItem",
                position: 1,
                name: "Home",
                item: "https://iamieshiadanielle.com",
              },
              {
                "@type": "ListItem",
                position: 2,
                name: "Programs",
                item: "https://iamieshiadanielle.com/programs",
              },
              {
                "@type": "ListItem",
                position: 3,
                name: "Workbook",
                item: "https://iamieshiadanielle.com/programs/workbook",
              },
            ],
          },
          {
            "@type": "Product",
            "@id": "https://iamieshiadanielle.com/programs/workbook#product",
            name: "From Burnout to Becoming Workbook",
            description:
              "64 guided pages to name the source of your burnout across the six B's (Beliefs, Belonging, Boundaries, Business, Budget, Body) and write a practical plan forward. Physical workbook plus instant digital access. Licensed for personal use only.",
            image:
              "https://vibe.filesafe.space/1784937135036191007/attachments/dd434134-7119-4daf-be69-edf4d039f7bb.png",
            brand: { "@id": "https://iamieshiadanielle.com/#organization" },
            aggregateRating: {
              "@type": "AggregateRating",
              ratingValue: "5",
              reviewCount: "3",
            },
            offers: [
              {
                "@type": "Offer",
                name: "Workbook (physical + instant digital access)",
                price: "27",
                priceCurrency: "USD",
                availability: "https://schema.org/InStock",
                url: "https://iamieshiadanielle.com/programs/workbook",
              },
              {
                "@type": "Offer",
                name: "Workbook Digital Download",
                price: "15",
                priceCurrency: "USD",
                availability: "https://schema.org/InStock",
                url: "https://iamieshiadanielle.com/programs/workbook",
              },
              {
                "@type": "Offer",
                name: "Identity Reset + Workbook Bundle (shipping included)",
                price: "67",
                priceCurrency: "USD",
                availability: "https://schema.org/InStock",
                url: "https://iamieshiadanielle.com/programs/workbook",
              },
            ],
            review: [
              {
                "@type": "Review",
                reviewBody:
                  "This helped me name what I was carrying instead of calling myself lazy",
                author: { "@type": "Person", name: "Workbook reader" },
              },
              {
                "@type": "Review",
                reviewBody:
                  "I finally had language for why I felt stuck and a practical place to begin",
                author: { "@type": "Person", name: "Workbook reader" },
              },
              {
                "@type": "Review",
                reviewBody:
                  "It felt like someone sat beside me and helped me find myself again",
                author: { "@type": "Person", name: "Workbook reader" },
              },
            ],
          },
          {
            "@type": "FAQPage",
            "@id": "https://iamieshiadanielle.com/programs/workbook#faq",
            mainEntity: [
              {
                "@type": "Question",
                name: "Is this only for moms?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "No. The workbook is for any woman of faith who has gone missing inside her own life: moms, wives, ministry leaders, entrepreneurs, and caregivers alike. The six B's framework meets you wherever your burnout is coming from.",
                },
              },
              {
                "@type": "Question",
                name: "How much time does it take?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "It is fully self-paced. The 64 guided pages are organized into five sections, so you can work through a few pages a day or sit down for longer sessions. Most women move through it over a few weeks alongside real life.",
                },
              },
              {
                "@type": "Question",
                name: "What do I receive?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "You receive the physical From Burnout to Becoming workbook shipped to you, plus instant digital access so you can begin the moment you order.",
                },
              },
              {
                "@type": "Question",
                name: "Is it Scripture-rooted?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "Yes. Every section pairs biblical principles with practical written exercises, so the truth you uncover turns into a plan you can actually walk out.",
                },
              },
              {
                "@type": "Question",
                name: "What is included in the $67 bundle?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "The bundle includes the Identity Reset workshop (the full teaching video with guided reflection) plus the physical workbook with instant digital access, and shipping is included.",
                },
              },
            ],
          },
        ]}
      />
      <div className="min-h-screen bg-[#FAF6F0] text-[#380C1E] flex flex-col font-sans">
        <Header />
        <main className="flex-1">
          <WorkbookHero />
          <WorkbookMiddleSections />
          <WorkbookBottomSections />
        </main>
        <Footer />
      </div>
    </>
  );
};

export default Workbook;
